<template>
  <v-container v-if="$can('browse', 'Owner')">
    <!--<editor-fold desc="Page header">-->
    <v-row

      align="center"
      class="mb-6"
    >
      <v-col>
        <div class="headline">
          Yacht Owners
        </div>
      </v-col>
      <v-col class="shrink">
        <v-btn
          v-if="$can('create', 'Owner')"
          color="primary"
          class="ma-0"
          @click="openOwnerEditDialog()"
        >
          <v-icon left>
            add
          </v-icon>
          Add New
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->

    <v-row>
      <v-col
        cols="3"
      >
        <v-subheader class="justify-space-between pr-0">
          Filters
          <v-btn
            v-if="isFiltered"
            text
            color="red"
            @click="clearFilters"
          >
            <v-icon
              small
              left
            >
              close
            </v-icon>
            Clear
          </v-btn>
        </v-subheader>
        <v-text-field
          v-model="filtersDirty.name"
          solo
          hide-details
          clearable
          label="Name"
          class="mb-2"
        />
        <v-autocomplete
          v-model="filtersDirty.country_id"
          solo
          label="Country"
          :items="countries.data"
          :loading="countries.loading"
          item-text="name"
          item-value="id"
          hide-details
          clearable
        />
      </v-col>
      <v-col cols="13">
        <!--<editor-fold desc="Pagination header">-->
        <pagination
          :from="owners.meta.from"
          :to="owners.meta.to"
          :total="owners.meta.total"
          :last-page="owners.meta.last_page"
          :page.sync="pagination.page"
          class="mb-1 align-end"
        />
        <!--</editor-fold>-->
        <!--<editor-fold desc="Table">-->
        <v-data-table
          :headers="headers"
          :items="owners.data"
          :loading="owners.loading"
          :page.sync="pagination.page"
          :sort-by.sync="sorting.sortBy"
          :sort-desc.sync="sorting.desc"
          :server-items-length="owners.meta.total"
          hide-default-footer
          must-sort
          class="elevation-5"
        >
          <template v-slot:item="{ item }">
            <owner-row
              :owner="item"
              @click.native="openOwnerEditDialog(item)"
            />
          </template>
          <template slot="no-data">
            <!--<editor-fold desc="Filtered">-->
            <v-row
              v-if="isFiltered"

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no results for your search..
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  class="ma-0"
                  small
                  text
                  @click="clearFilters()"
                >
                  <v-icon left>
                    clear
                  </v-icon>
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
            <!--<editor-fold desc="NoData">-->
            <v-row
              v-else

              class="text-center py-2"
            >
              <v-col cols="16">
                There are no yacht owners at the moment.
              </v-col>
              <v-col
                cols="16"
                class="mt-2"
              >
                <v-btn
                  color="primary"
                  class="ma-0"
                  small
                  @click="openOwnerEditDialog()"
                >
                  <v-icon left>
                    add
                  </v-icon>
                  Add New
                </v-btn>
              </v-col>
            </v-row>
            <!--</editor-fold>-->
          </template>
          <table-footer
            slot="footer"
            :last-page="owners.meta.last_page"
            :page.sync="pagination.page"
            :colspan="7"
          />
        </v-data-table>
        <!--</editor-fold>-->
      </v-col>
    </v-row>

    <owner-edit-dialog
      :visible.sync="ownerEditDialog"
      :owner="ownerDirty"
      @owner:created="ownerCreated"
      @owner:updated="ownerUpdated"
      @owner:deleted="ownerDeleted"
    />
  </v-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import { mapGetters } from 'vuex'
import paginationMixin from '@/mixins/paginationMixin'
import filterableMixin from '@/mixins/filterableMixin'
import Owner from '@/models/Owner'
import OwnerRow from '@/components/business/owners/OwnerRow'
import OwnerEditDialog from '@/components/business/owners/OwnerEditDialog'
import TableFooter from '@/components/helpers/TableFooter'

export default {
    name: 'OwnersIndex',
    components: {
      OwnerRow,
      OwnerEditDialog,
      TableFooter
    },
    mixins: [paginationMixin, filterableMixin],
    data() {
      return {
        owners: {
          data: [],
          loading: false,
          meta: {}
        },
        headers: [
          { text: 'ID', value: 'id', sortable: true },
          { text: 'Name', value: 'name', sortable: true },
          { text: 'Yachts', value: 'yachts', sortable: false },
          { text: 'Email', value: 'contact_details.email', sortable: false },
          { text: 'Phone', value: 'contact_details.phone', sortable: false },
          { text: 'User Account', value: 'has_user', sortable: false },
          { text: 'Country', value: 'country_id', sortable: false }
        ],
        ownerDirty: undefined,
        ownerEditDialog: false,
        filtersClean: Owner.emptyFilters,
        filtersDirty: cloneDeep(Owner.emptyFilters)
      }
    },
    computed: {
      ...mapGetters('settings', ['countries'])
    },
    created() {
      this.fetchOwners()
    },
    methods: {
      onPaginationUpdate() {
        this.fetchOwners()
      },
      onFiltersUpdate() {
        this.resetPagination()
      },
      async fetchOwners() {
        this.owners.loading = true

        let queryParams = {
          ...this.paginationQueryParams,
          ...this.filterQueryParams
        }

        try {
          let result = await Owner.api.getOwners(queryParams)

          this.owners.data = result.data
          this.owners.meta = result.meta
        } catch (e) {
          this.$store.dispatch('alert', {
            'text': 'Something went wrong',
            'color': 'error'
          })
          throw e
        } finally {
          this.owners.loading = false
        }
      },
      openOwnerEditDialog(owner = undefined) {
        if (!this.$can('edit', 'Owner')) return

        this.ownerDirty = owner
        this.ownerEditDialog = true
      },
      clearFilters() {
        this.filters = cloneDeep(Owner.emptyFilters)
      },
      ownerCreated(owner) {
        this.owners.data.unshift(owner)
      },
      ownerUpdated(owner) {
        const index = this.owners.data.findIndex(o => o.id === owner.id)

        this.owners.data.splice(index, 1, owner)
      },
      ownerDeleted(ownerId) {
        const index = this.owners.data.findIndex(o => o.id === ownerId)

        this.owners.data.splice(index, 1)
      }
    }
  }
</script>
